import { Routes, Route } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { LanguageProvider } from './hooks/useLanguage'
import { CookieConsentProvider } from './components/cookie-consent/CookieConsentProvider'
import RouteChangeTracker from './components/cookie-consent/RouteChangeTracker'
import CustomBanner from './components/cookie-consent/CustomBanner'
import PageTitleManager from './components/PageTitleManager'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { AdminRoute } from './components/auth/AdminRoute'
import Layout from './components/layout/Layout'
import Navbar from './components/layout/Navbar'
import Footer from './components/layout/Footer'

// Import pages
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import GoogleCallbackPage from './pages/GoogleCallbackPage'
import VerifyEmailPage from './pages/VerifyEmailPage'
import PasswordResetPage from './pages/PasswordResetPage'
import AccountPage from './pages/AccountPage'
import DemoAppPage from './pages/DemoAppPage'
import FileUploadPage from './pages/FileUploadPage'
import CheckoutPage from './pages/CheckoutPage'

import ContactPage from './pages/ContactPage'
import AboutPage from './pages/AboutPage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import TermsPage from './pages/TermsPage'
import AdminDashboardPage from './pages/AdminDashboardPage'
import AdminUsersPage from './pages/AdminUsersPage'
import AdminMessagesPage from './pages/AdminMessagesPage'
import AdminAnalyticsPage from './pages/AdminAnalyticsPage'
import TripDetailsPage from './pages/TripDetailsPage'
import ActivityDetailsPage from './pages/ActivityDetailsPage'
import DestinationDetailsPage from './pages/DestinationDetailsPage'
import CuriosityDetailsPage from './pages/CuriosityDetailsPage'
import PaymentReturnPage from './pages/PaymentReturnPage'
import PaymentErrorPage from './pages/PaymentErrorPage'
import TestPaymentPage from './pages/TestPaymentPage'
import UserProfilePage from './pages/UserProfilePage'
import ProductsPage from './pages/ProductsPage'
import ProductDetailsPage from './pages/ProductDetailsPage'
import CartPage from './pages/CartPage'
import ProductPaymentSuccessPage from './pages/ProductPaymentSuccessPage'
import ProductPaymentCancelledPage from './pages/ProductPaymentCancelledPage'
import ShippingCostsPage from './pages/ShippingCostsPage'
import NotFoundPage from './pages/NotFoundPage'

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <CookieConsentProvider>
          <CustomBanner />
          <RouteChangeTracker />
          <PageTitleManager />
          <Routes>
          {/* Landing Page - No Layout */}
          <Route path="/" element={<LandingPage />} />
          
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/password-reset" element={<PasswordResetPage />} />
          <Route path="/reset-password" element={<PasswordResetPage />} />

          <Route path="/contact" element={<ContactPage />} />
          <Route path="/about-us" element={<AboutPage />} />
          <Route path="/shipping-costs" element={<ShippingCostsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-and-conditions" element={<TermsPage />} />
          
          {/* Trip, Activity, Destination and Curiosity Routes */}
          <Route path="/trip/:tripId" element={<TripDetailsPage />} />
          <Route path="/activities/:activityId" element={<ActivityDetailsPage />} />
          <Route path="/destination/:destinationId" element={<DestinationDetailsPage />} />
          <Route path="/curiosities/:curiosityId" element={<CuriosityDetailsPage />} />
          
          {/* Product Routes */}
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/:category/:productSlug" element={<ProductDetailsPage />} />
          
          {/* Protected Product Routes */}
          <Route path="/cart" element={
            <ProtectedRoute>
              <Layout fullWidth={true}>
                <CartPage />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/checkout" element={
            <ProtectedRoute>
              <Layout>
                <CheckoutPage />
              </Layout>
            </ProtectedRoute>
          } />
          
          {/* Payment Routes - These should be public and accessible */}
          {/* Make sure these routes are placed before the 404 route */}
          <Route path="/payment-return" element={<PaymentReturnPage />} />
          <Route path="/payment-success" element={<PaymentReturnPage />} />
          <Route path="/payment-cancel" element={<PaymentReturnPage />} />
          <Route path="/payment-error" element={<PaymentErrorPage />} />
          <Route path="/test-payment" element={<TestPaymentPage />} />
          
          {/* Product Payment Routes */}
          <Route path="/product-payment-success" element={<ProductPaymentSuccessPage />} />
          <Route path="/product-payment-cancelled" element={<ProductPaymentCancelledPage />} />
          
          {/* Fallback payment routes for any payment-related URLs */}
          <Route path="/payment/*" element={<PaymentReturnPage />} />
          
          {/* Protected Routes */}
          <Route path="/account" element={
            <ProtectedRoute>
              <Layout fullWidth={true}>
                <AccountPage />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/profile" element={
            <ProtectedRoute>
              <UserProfilePage />
            </ProtectedRoute>
          } />
          
          <Route path="/demo-app" element={
            <ProtectedRoute>
              <Layout>
                <DemoAppPage />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/file-upload" element={
            <ProtectedRoute>
              <Layout>
                <FileUploadPage />
              </Layout>
            </ProtectedRoute>
          } />
          
          {/* Admin Routes */}
          <Route path="/admin" element={
            <AdminRoute>
              <Layout hideFooter={true} hideHeader={true} fullWidth={true}>
                <AdminDashboardPage />
              </Layout>
            </AdminRoute>
          } />
          
          <Route path="/admin/users" element={
            <AdminRoute>
              <Layout hideFooter={true} hideHeader={true}>
                <AdminUsersPage />
              </Layout>
            </AdminRoute>
          } />
          
          <Route path="/admin/messages" element={
            <AdminRoute>
              <Layout hideFooter={true} hideHeader={true}>
                <AdminMessagesPage />
              </Layout>
            </AdminRoute>
          } />
          
          <Route path="/admin/analytics" element={
            <AdminRoute>
              <Layout hideFooter={true} hideHeader={true}>
                <AdminAnalyticsPage />
              </Layout>
            </AdminRoute>
          } />
          
          {/* 404 Route - This should be the last route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </CookieConsentProvider>
      </AuthProvider>
    </LanguageProvider>
  )
}

export default App 